import { ImobiliarioEdificacao } from "../interfaces";
import { getAll, getOne } from "../services/db";
import {
  isVinculoEdificacaoAtivo,
  normalizeTipoVinculo,
  ordenarVinculosEdificacaoPorStatus,
  TIPO_VINCULO_EDIFICACAO,
} from "./edificacoesHierarchy";

export type AcaoVinculoEdificacaoRioBranco =
  | "principal"
  | "nova_unidade"
  | "subunidade_imovel"
  | "complementar_principal"
  | "complementar_subunidade";

export function getVinculosAtivosDoImobiliario(
  vinculos: ImobiliarioEdificacao[],
  idImobiliario: string | number,
) {
  return vinculos.filter(
    (vinculo) =>
      String(vinculo.id_imobiliario) === String(idImobiliario) && isVinculoEdificacaoAtivo(vinculo),
  );
}

export function imobiliarioTemPrincipalAtiva(
  vinculos: ImobiliarioEdificacao[],
  idImobiliario: string | number,
): boolean {
  return getVinculosAtivosDoImobiliario(vinculos, idImobiliario).some(
    (vinculo) => Number(vinculo.tipo_vinculo) === TIPO_VINCULO_EDIFICACAO.PRINCIPAL,
  );
}

export function getPrincipalAtiva(
  vinculos: ImobiliarioEdificacao[],
  idImobiliario: string | number,
) {
  return ordenarVinculosEdificacaoPorStatus(getVinculosAtivosDoImobiliario(vinculos, idImobiliario)).find(
    (vinculo) => Number(vinculo.tipo_vinculo) === TIPO_VINCULO_EDIFICACAO.PRINCIPAL,
  );
}

export function getSubunidadesAtivas(
  vinculos: ImobiliarioEdificacao[],
  idImobiliario: string | number,
) {
  return ordenarVinculosEdificacaoPorStatus(getVinculosAtivosDoImobiliario(vinculos, idImobiliario)).filter(
    (vinculo) => Number(vinculo.tipo_vinculo) === TIPO_VINCULO_EDIFICACAO.SUBUNIDADE,
  );
}

export function proximaInscricaoSubunidade(
  vinculos: ImobiliarioEdificacao[],
  idImobiliario: string | number,
  inscricaoUnidade?: string | null,
): string | undefined {
  if (!inscricaoUnidade) return undefined;

  const prefixo = String(inscricaoUnidade).slice(0, -3);
  const subunidades = getSubunidadesAtivas(vinculos, idImobiliario).filter((vinculo) =>
    String(vinculo.inscricao_subunidade || "").startsWith(prefixo),
  );

  let maior = 901;
  subunidades.forEach((vinculo) => {
    const sufixo = String(vinculo.inscricao_subunidade || "").slice(-3);
    if (/^\d+$/.test(sufixo)) {
      maior = Math.max(maior, Number(sufixo));
    }
  });

  return `${prefixo}${String(maior + 1).padStart(3, "0")}`;
}

export function edificacaoJaVinculadaAoImobiliario(
  vinculos: ImobiliarioEdificacao[],
  idImobiliario: string | number,
  idEdificacao: string | number,
) {
  return getVinculosAtivosDoImobiliario(vinculos, idImobiliario).some(
    (vinculo) => String(vinculo.id_edificacao) === String(idEdificacao),
  );
}

export function getNumeroCadastroPrincipalRioBranco(
  idImobiliario?: string | number,
): number | string | null | undefined {
  if (!idImobiliario) return undefined;

  const vinculos = (getAll("imobiliario_edificacao") || []) as ImobiliarioEdificacao[];
  const principal = getPrincipalAtiva(vinculos, idImobiliario);
  if (principal?.numero_cadastro != null) return principal.numero_cadastro;

  const imobiliario = getOne(String(idImobiliario), "imobiliario");
  return imobiliario?.numero_cadastro;
}

/**
 * Remove conflitos de `numero_cadastro` permitidos entre principal e complementares no mesmo imobiliário.
 */
export function filtrarConflitosNumeroCadastroPermitidosRioBranco(
  vinculoAtual: ImobiliarioEdificacao,
  conflitos: ImobiliarioEdificacao[],
  idImobiliarioContexto?: string | number,
): ImobiliarioEdificacao[] {
  const tipoAtual = normalizeTipoVinculo(vinculoAtual.tipo_vinculo);
  const idImobiliario = String(idImobiliarioContexto ?? vinculoAtual.id_imobiliario);

  return conflitos.filter((conflito) => {
    const tipoConflito = normalizeTipoVinculo(conflito.tipo_vinculo);

    if (tipoAtual === TIPO_VINCULO_EDIFICACAO.COMPLEMENTAR) {
      const numeroPrincipal = getNumeroCadastroPrincipalRioBranco(idImobiliario);
      const conflitoEhPrincipalPermitido =
        tipoConflito === TIPO_VINCULO_EDIFICACAO.PRINCIPAL &&
        String(conflito.id_imobiliario) === idImobiliario &&
        numeroPrincipal != null &&
        String(numeroPrincipal) === String(conflito.numero_cadastro);

      return !conflitoEhPrincipalPermitido;
    }

    if (tipoAtual === TIPO_VINCULO_EDIFICACAO.PRINCIPAL) {
      const conflitoEhComplementarPermitido =
        tipoConflito === TIPO_VINCULO_EDIFICACAO.COMPLEMENTAR &&
        String(conflito.id_imobiliario) === idImobiliario;

      return !conflitoEhComplementarPermitido;
    }

    return true;
  });
}
